import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { DriveItem } from "../types";
import {
  buildOfficeViewerUrl,
  buildUploadUrl,
  getIconByMime,
  getPreviewKind,
} from "../helpers";

/**
 * - "card"  : fixed 250px cover box used in the My Folder grid.
 * - "thumb" : 150px thumbnail used in the Shared / Official grids (also previews text files).
 * - "modal" : full-screen preview inside the file preview modal.
 */
type PreviewVariant = "card" | "thumb" | "modal";

interface FilePreviewProps {
  item: DriveItem;
  variant: PreviewVariant;
  /** Wrapper double-click (card & thumb open the item). */
  onDoubleClick?: () => void;
  /** Modal fallback download button. */
  onDownload?: (item: DriveItem) => void;
}

const audioFallback = "Your browser does not support the audio element.";

/** Renders the right media element for a file, sized to the requested variant. */
const FilePreview = ({
  item,
  variant,
  onDoubleClick,
  onDownload,
}: FilePreviewProps) => {
  const url = buildUploadUrl(item.path);
  const officeUrl = buildOfficeViewerUrl(item.path);
  const kind = getPreviewKind(item, { previewText: variant === "thumb" });
  const iconSrc = `assets/img/icons/${getIconByMime(item)}`;

  if (variant === "card") {
    return (
      <div
        onDoubleClick={onDoubleClick}
        className="preview-box"
        style={{
          width: "100%",
          height: "250px",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f9f9f9",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {kind === "image" ? (
          <img
            src={url}
            alt={item.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
            }}
            loading="lazy"
            draggable={false}
            onError={(e) => {
              e.currentTarget.src = "assets/img/placeholders/image-fallback.png";
            }}
          />
        ) : kind === "pdf" ? (
          <iframe
            src={url}
            title={item.name}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              overflow: "hidden",
            }}
          />
        ) : kind === "video" ? (
          <video
            src={url}
            controls
            style={{ width: "100%", height: "100%", objectFit: "fill" }}
          />
        ) : kind === "audio" ? (
          <audio controls style={{ width: "95%" }}>
            <source src={url} type={item.mime_type} />
            {audioFallback}
          </audio>
        ) : kind === "office" ? (
          <iframe
            src={officeUrl}
            title={item.name}
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        ) : (
          <ImageWithBasePath src={iconSrc} alt={item.name} width={50} height={50} />
        )}
      </div>
    );
  }

  if (variant === "thumb") {
    return (
      <div onDoubleClick={onDoubleClick} style={{ display: "flex", justifyContent: "center" }}>
        {kind === "image" ? (
          <img
            src={url}
            alt={item.name}
            style={{ width: "100%", height: "150px", objectFit: "cover", borderRadius: 6 }}
            className="mb-3"
          />
        ) : kind === "pdf" ? (
          <iframe
            src={url}
            title={item.name}
            style={{ width: "100%", height: "150px", border: "none", borderRadius: 6 }}
            className="mb-3"
          />
        ) : kind === "video" ? (
          <video
            src={url}
            controls
            style={{ width: "100%", height: "150px", borderRadius: 6 }}
            className="mb-3"
          />
        ) : kind === "audio" ? (
          <audio controls style={{ width: "100%", marginBottom: "12px" }}>
            <source src={url} type={item.mime_type} />
            {audioFallback}
          </audio>
        ) : kind === "text" ? (
          <iframe
            src={url}
            title={item.name}
            style={{ width: "100%", height: "150px", border: "1px solid #ccc", borderRadius: 6 }}
            className="mb-3"
          />
        ) : kind === "office" ? (
          <iframe
            src={officeUrl}
            title={item.name}
            style={{ width: "100%", height: "150px", border: "1px solid #ccc", borderRadius: 6 }}
            className="mb-3"
          />
        ) : (
          <ImageWithBasePath
            src={iconSrc}
            alt={item.name}
            className="mb-3"
            width={50}
            height={32}
          />
        )}
      </div>
    );
  }

  // variant === "modal"
  return (
    <>
      {kind === "image" ? (
        <img
          src={url}
          alt={item.name}
          style={{
            maxWidth: "calc(100% - 32px)",
            maxHeight: "78vh",
            objectFit: "contain",
            margin: 16,
            borderRadius: 10,
            boxShadow: "0 12px 32px rgba(0,0,0,0.28)",
          }}
          onError={(e) => {
            e.currentTarget.src = "assets/img/placeholders/image-fallback.png";
          }}
        />
      ) : kind === "pdf" ? (
        <iframe
          src={url}
          title={item.name}
          style={{ width: "100%", height: "80vh", border: "none" }}
        />
      ) : kind === "video" ? (
        <video
          src={url}
          controls
          autoPlay
          style={{ maxWidth: "100%", maxHeight: "80vh" }}
        />
      ) : kind === "audio" ? (
        <div className="bg-white p-4 rounded text-center">
          <audio controls autoPlay>
            <source src={url} type={item.mime_type} />
            {audioFallback}
          </audio>
          <p className="mt-3">{item.name}</p>
        </div>
      ) : kind === "office" ? (
        <iframe
          src={officeUrl}
          title={item.name}
          style={{ width: "100%", height: "80vh", border: "none" }}
        />
      ) : (
        <div className="bg-white p-4 rounded text-center">
          <ImageWithBasePath src={iconSrc} alt={item.name} width={64} height={64} />
          <p className="mt-3">{item.name}</p>
          <button
            className="btn btn-primary mt-2"
            onClick={() => onDownload?.(item)}
          >
            Download File
          </button>
        </div>
      )}
    </>
  );
};

export default FilePreview;
